import { useState, useCallback, useEffect } from "react";
import { ReactTagManager } from "react-gtm-ts";
import { Navigate, useNavigate } from "react-router-dom";
import appConfig from "@/config/app-config";
import Button from "@/components/Button";
import BankIdLogo from "@/assets/icons/BankID_logo.svg?react";
import BankIdLogoWhite from "@/assets/icons/BankID_logo_white.svg?react";
import Chevron from "@/assets/icons/chevron.svg?react";
import { useAuth } from "@/hooks/useAuth";
import useVisibilityChange from "@/hooks/useVisibilityChange";
import Visible from "@/components/Visible";
import Spinner from "@/components/Spinner";
import Platform from "@/components/Platform";
import styles from "./Pages.module.scss";
import { useOnboarding } from "@/hooks/useOnboarding";
import Container from "@/components/Container";
import ChatBubble from "@/components/ChatBubble";
import Answered from "@/components/Answered";
import Page from "@/components/Page";

export default function Recommendation() {
  const { loginState, setLoginState } = useAuth();
  const { questionnaireCompleted } = useOnboarding();
  const navigate = useNavigate();

  useEffect(() => {
    if (!questionnaireCompleted) {
      return;
    }

    ReactTagManager.action({
      event: "web_onboarding_finish_questionnaire",
    });
  }, [questionnaireCompleted]);

  if (!questionnaireCompleted) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container>
      {loginState === "idle" && <PreSignupInfo />}
      {loginState !== "idle" && (
        <LoginFlow
          onSuccess={() => {
            navigate("/ladda-ner");
            setLoginState("idle");
          }}
          onConflict={() => {
            navigate("/redan-registrerad");
            setLoginState("idle");
          }}
        />
      )}
    </Container>
  );
}

const Login = () => {
  const { setLoginState, loginOnDevice, beginQrLoginFlow } = useAuth();

  return (
    <div className="stack-1">
      <Platform mobile>
        <Button
          onClick={loginOnDevice}
          iconLeft={<BankIdLogoWhite width={22} />}
          iconRight={<Chevron />}
          fullWidth
        >
          BankID på den här enheten
        </Button>
        <Button
          onClick={beginQrLoginFlow}
          variant="tertiary"
          iconLeft={<BankIdLogo width={22} />}
          iconRight={<Chevron />}
          fullWidth
        >
          BankID på annan enhet
        </Button>
      </Platform>
      <Platform desktop>
        <div className="text-center">
          <Button
            onClick={beginQrLoginFlow}
            iconLeft={<BankIdLogoWhite width={22} />}
            iconRight={<Chevron />}
          >
            Logga in med Mobilt BankID
          </Button>
        </div>
      </Platform>
      <Visible when={appConfig.devLoginEnabled}>
        <div className="text-center">
          <Button
            variant="secondary"
            onClick={() => setLoginState("enter_identifier")}
          >
            Logga in med personnummer (DEV)
          </Button>
        </div>
      </Visible>

      <div className={styles.disclaimer}>
        Genom att fortsätta accepterar du Blodtrycksdoktorns{" "}
        <a
          href="https://blodtrycksdoktorn.se/anvandarvillkor/"
          target="_blank"
          rel="noreferrer"
        >
          användarvillkor
        </a>{" "}
        och{" "}
        <a
          href="https://blodtrycksdoktorn.se/integritetspolicy/"
          target="_blank"
          rel="noreferrer"
        >
          integritetspolicy
        </a>
        .
      </div>
    </div>
  );
};

const PreSignupInfo = () => {
  const { currentOnboardingFlow } = useOnboarding();

  return currentOnboardingFlow === "obesity" ? (
    <ObesityInfo>
      <Login />
    </ObesityInfo>
  ) : (
    <HypertensionInfo>
      <Login />
    </HypertensionInfo>
  );
};

const ObesityInfo = ({ children }: React.PropsWithChildren) => {
  const existingTreatmentQuestionId = "q5";

  const infoSection = (
    <>
      <p>
        Tack för dina svar! Nu behöver du bara logga in med BankID för att
        skicka in dem.
      </p>

      <p>
        Blodtrycksdoktorn är en registrerad svensk vårdgivare. BankID krävs för
        trygg och säker identifiering.
      </p>

      <p>Du förbinder dig inte till något och kan avsluta när du vill. </p>
    </>
  );

  return (
    <Page>
      <div className="mb-30">
        <ChatBubble>
          Hej, jag heter Qim och jag är verksamhetschef på Blodtrycksdoktorn.
        </ChatBubble>
      </div>

      <Answered affirmative to={existingTreatmentQuestionId}>
        <h1>Fortsätt din överviktsbehandling hos oss</h1>

        <section>{infoSection}</section>
      </Answered>

      <Answered negative to={existingTreatmentQuestionId}>
        <h1>Starta din överviktsbehandling </h1>

        <section>{infoSection}</section>
      </Answered>

      <p className={styles.noCommitment}>
        Ingen bindningstid — avsluta när som helst
      </p>

      {children}

      <div className={styles.additionalInfo}>
        <strong>Tänk på att</strong>
        <ul>
          <li>Kostnader för läkemedel tillkommer</li>
          <li>
            Vår överviktsbehandling är idag inte utformad för att hjälpa
            personer med hjärt-njursvikt, inflammatorisk tarmsjukdom, pågående
            cancerbehandling, insulinbehandlad diabetes eller under tiden som du
            är gravid.
          </li>
        </ul>
      </div>
    </Page>
  );
};

const HypertensionInfo = ({ children }: React.PropsWithChildren) => {
  const exstingDiagnosisQuestionId = "q1";

  const infoSection = (
    <>
      <Answered affirmative to={exstingDiagnosisQuestionId}>
        <p>
          Tack för ditt svar! Nu behöver du bara logga in med BankID för att
          komma igång.
        </p>
      </Answered>

      <Answered negative to={exstingDiagnosisQuestionId}>
        <p>
          Tack för dina svar! Nu behöver du bara logga in med BankID för att
          komma igång.
        </p>
      </Answered>

      <p>
        Blodtrycksdoktorn är en registrerad svensk vårdgivare. BankID krävs för
        trygg och säker identifiering.
      </p>
    </>
  );

  return (
    <Page>
      <div className="mb-30">
        <ChatBubble>
          Visste du att ca 2,3 miljoner svenskar har högt blodtryck och hälften
          vet inte om det?
        </ChatBubble>
      </div>

      <Answered affirmative to={exstingDiagnosisQuestionId}>
        <h1>Kom igång med behandling för högt blodtryck</h1>

        <section>{infoSection}</section>
      </Answered>

      <Answered negative to={exstingDiagnosisQuestionId}>
        <h1>Kom igång med utredning för högt blodtryck</h1>

        <section>{infoSection}</section>
      </Answered>

      <p className={styles.noCommitment}>
        Ingen bindningstid — avsluta när som helst
      </p>

      {children}

      <div className={styles.additionalInfo}>
        <strong>Tänk på att</strong>
        <ul>
          <li>
            Behandling eller utredning för högt blodtryck är idag inte utformad
            för att hjälpa personer med hjärt-njursvikt eller under tiden som du
            är gravid.
          </li>
        </ul>
      </div>
    </Page>
  );
};

type LoginFlowProps = {
  onSuccess: () => void;
  onConflict: () => void;
};

const LoginFlow = ({ onSuccess, onConflict }: LoginFlowProps) => {
  const [identifier, setIdentifier] = useState("");
  const { loginWithCredentials, completeLogin, loginState, setLoginState } =
    useAuth();

  const completeLoginWithStoredRefToken = useCallback(() => {
    const storedRefToken = localStorage.getItem("refToken");
    console.log("ref", storedRefToken);

    if (storedRefToken) {
      console.log("Found a stored ref, completing login");
      void completeLogin(storedRefToken)
        .then(onSuccess)
        .catch((error: Error) => {
          if (error.message === "Conflict") {
            onConflict();
          }
        });
    }
  }, [completeLogin, onSuccess]);

  useVisibilityChange(completeLoginWithStoredRefToken);

  const loginDev = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    try {
      await loginWithCredentials(identifier);
      onSuccess();
    } catch (error: any) {
      if (error.message === "Conflict") {
        onConflict();
      }
      console.log(e);
    }
  };

  return (
    <div className={styles.pageContent}>
      <Visible when={loginState === "enter_identifier"}>
        <form onSubmit={loginDev} className="stack-1">
          <div>
            <label>
              Personnummer
              <input
                autoFocus
                type="text"
                id="auth-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoComplete="off"
              />
            </label>
          </div>
          <Button type="submit">Logga in</Button>
        </form>
        <Button variant="text" onClick={() => setLoginState("idle")}>
          Avbryt
        </Button>
      </Visible>

      <Visible
        when={[
          "waiting_for_bankid",
          "starting",
          "polling",
          "user_signing",
          "authenticated",
        ].includes(loginState)}
      >
        <BankIdPrompt onSuccess={onSuccess} />
      </Visible>

      <Visible when={loginState === "error"}>
        <div className="text-center">
          <div className={styles.error}>Något gick fel.</div>
          <Button variant="secondary" onClick={() => setLoginState("idle")}>
            Försök igen
          </Button>
        </div>
      </Visible>
    </div>
  );
};

const BankIdPrompt = ({ onSuccess }: Pick<LoginFlowProps, "onSuccess">) => {
  const { loginState, activeQrCode, cancelLogin } = useAuth();

  useEffect(() => {
    if (loginState === "authenticated") {
      onSuccess();
    }
  }, [loginState, onSuccess]);

  return (
    <>
      <Visible when={loginState === "waiting_for_bankid"}>
        <div className="text-center stack-2">
          <div>
            <Spinner size="large" />
          </div>
          <div>
            BankID appen kommer att startas automatiskt om den är installerad på
            den här enheten.
          </div>
          <div>
            <Button variant="text" onClick={cancelLogin}>
              Avbryt inloggning
            </Button>
          </div>
        </div>
      </Visible>

      <Visible when={["starting", "polling"].includes(loginState)}>
        <div className="text-center stack-2 mt-40">
          {activeQrCode ? (
            <div className="flex justify-center">
              <div
                className={styles.qrWrapper}
                dangerouslySetInnerHTML={{
                  __html: activeQrCode,
                }}
              />
            </div>
          ) : (
            <div>
              <Spinner size="large" />
            </div>
          )}
          <div>
            Öppna appen Mobilt BankID på din mobiltelefon och tryck på
            QR-symbolen. Följ instruktionerna i appen.
          </div>
          <Button variant="text" onClick={cancelLogin}>
            Avbryt inloggning
          </Button>
        </div>
      </Visible>
      <Visible when={["user_signing", "authenticated"].includes(loginState)}>
        <div>
          Skriv in din säkerhetskod i BankID-appen och välj{" "}
          <strong>Identifiera</strong>.
        </div>
      </Visible>
    </>
  );
};
